import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import {
  LuArrowLeft, LuCheck, LuLoaderCircle, LuLock, LuNotebookPen, LuRadio, LuScreenShare,
  LuScreenShareOff, LuTimer, LuTriangleAlert,
} from 'react-icons/lu'
import { isNoteFinalized, writeCachedAstrologerNote } from '../bookingDetails/bookingDetailsData'
import DictationButton from '../../common/DictationButton'
import { appendSpokenText } from '../../../hooks/useSpeechToText'

/**
 * SDK-agnostic pieces of the consultation room, shared by the VideoSDK room
 * (VideosdkConsultationRoom) and the LiveKit room (LiveKitConsultationRoom).
 * Nothing here touches a media SDK: it is the call's look and the astrologer's
 * side tools — shell, buttons, countdown pill, side cards, the autosaving
 * notes card, the permission pre-check and the countdown anchor parser — so
 * both rooms render identically and only the media layer differs. Lifted out
 * of VideoConsultationPage.jsx unchanged when the provider switch landed.
 */

/**
 * "2026-07-20 10:30:00" (stored as UTC, same normalization the legacy portal
 * applies) → epoch ms, or null. Anchors the countdown across page reloads.
 * Only trusted once the booking reads in_progress (the seeker stamped it live).
 */
export function parseMeetingStartedAt(booking) {
  if (String(booking?.status || '').toLowerCase() !== 'in_progress') return null
  if (!booking?.meeting_started_at) return null
  let s = String(booking.meeting_started_at).replace(' ', 'T')
  if (s.length >= 19 && !/Z$/i.test(s) && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

/**
 * Settle the camera/mic permission prompt before a room that auto-joins is
 * allowed to mount. Returns true once the browser has answered (granted OR
 * denied) — a denial still lets the join proceed, it just publishes no camera,
 * which the seeker can fix from the in-call toggle once they allow the device.
 * The probe tracks are stopped immediately; the SDK opens its own.
 */
export function useMediaPermission(gate, wantVideo) {
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    if (!gate) return undefined
    let cancelled = false
    const done = () => { if (!cancelled) setSettled(true) }
    const media = navigator.mediaDevices
    if (!media?.getUserMedia) { done(); return undefined }
    media.getUserMedia({ audio: true, video: wantVideo })
      .then((stream) => { stream.getTracks().forEach((track) => track.stop()) })
      .catch(() => { /* denied / no device — join anyway, device toggles stay */ })
      .finally(done)
    return () => { cancelled = true }
  }, [gate, wantVideo])
  return !gate || settled
}

/**
 * True when the incoming track is taller than it is wide — i.e. the other end
 * is holding a phone upright. Read from the <video>'s INTRINSIC size rather
 * than assumed from the viewer's own screen: a desktop astrologer is usually
 * receiving a portrait phone frame, and a phone seeker a landscape one. The
 * `resize` listener matters as much as `loadedmetadata` — the track's
 * dimensions change live when the publisher rotates their device mid-call.
 */
export function useVideoPortrait(videoRef, active) {
  const [portrait, setPortrait] = useState(false)
  useEffect(() => {
    const el = videoRef.current
    if (!el || !active) {
      setPortrait(false)
      return undefined
    }
    const read = () => {
      const { videoWidth: w, videoHeight: h } = el
      if (w > 0 && h > 0) setPortrait(h > w)
    }
    read()
    el.addEventListener('loadedmetadata', read)
    el.addEventListener('resize', read)
    return () => {
      el.removeEventListener('loadedmetadata', read)
      el.removeEventListener('resize', read)
    }
  }, [videoRef, active])
  return portrait
}

/* ── chrome ── */

export function CallShell({ children }) {
  return (
    <div className="min-h-dvh bg-[#14100D] flex flex-col items-center justify-center text-center px-4 sm:px-6 py-10">
      {children}
    </div>
  )
}

export function BackButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full px-6 sm:px-8 py-3 sm:py-3.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.12em] sm:tracking-[0.2em] border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors"
    >
      <LuArrowLeft className="w-3.5 h-3.5 shrink-0" /> {label}
    </button>
  )
}

/** The mm:ss countdown pill in the top bar — red and pulsing in the last two minutes. */
export function CountdownPill({ remaining }) {
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0')
  const ss = String(remaining % 60).padStart(2, '0')
  const lowTime = remaining <= 120
  return (
    <div className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-bold tabular-nums border ${
      lowTime ? 'border-red-400/40 bg-red-500/15 text-red-300 animate-pulse' : 'border-[#E6C878]/25 bg-[#E6C878]/10 text-[#E6C878]'
    }`}>
      <LuTimer className="w-3.5 h-3.5 shrink-0" /> {mm}:{ss}
    </div>
  )
}

/** Tiny corner badge naming the video service this call runs on. The provider
 * is the admin's choice (General Settings → Video Providers), locked per
 * booking — the badge is how either party can tell which one they are on when
 * something needs reporting. Muted on purpose: information, not decoration. */
export function ProviderBadge({ provider }) {
  const label = provider === 'livekit' ? 'LiveKit' : provider === 'videosdk' ? 'VideoSDK' : provider ? String(provider) : null
  if (!label) return null
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1 rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/40 shrink-0"
      title={`Live video via ${label}`}
      aria-label={`Video provider: ${label}`}
    >
      <LuRadio className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
      {label}
    </span>
  )
}

export function ControlButton({ active, onClick, OnIcon, OffIcon, title, disabled = false }) {
  // Off state (device disabled/muted) shows the crossed-out icon; that's also
  // what a hard-disabled control (e.g. camera in an audio-only session) reads.
  const Icon = !disabled && active ? OnIcon : OffIcon
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`w-11 h-11 min-w-11 sm:w-13 sm:h-13 sm:min-w-13 rounded-full flex items-center justify-center border transition-colors ${
        disabled
          ? 'bg-white/5 border-white/10 text-white/25 cursor-not-allowed'
          : active
            ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
            : 'bg-red-500/20 border-red-400/40 text-red-300 hover:bg-red-500/30'
      }`}
    >
      <Icon className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
    </button>
  )
}

/**
 * Screen-share toggle. Its neutral "off" state is deliberately NOT the red
 * alert styling the mic/webcam controls use for a muted device — not sharing
 * is the resting state, not a warning — and it turns gold while a share is
 * live.
 */
export function ScreenShareButton({ sharing, disabled = false, title, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`w-11 h-11 min-w-11 sm:w-13 sm:h-13 sm:min-w-13 rounded-full flex items-center justify-center border transition-colors ${
        disabled
          ? 'bg-white/5 border-white/10 text-white/25 cursor-not-allowed'
          : sharing
            ? 'bg-[#E6C878]/20 border-[#E6C878]/50 text-[#E6C878] hover:bg-[#E6C878]/30'
            : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
      }`}
    >
      {sharing
        ? <LuScreenShareOff className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
        : <LuScreenShare className="w-4.5 h-4.5 sm:w-5 sm:h-5" />}
    </button>
  )
}

/* ── astrologer console sidebar ── */

export function HostSideCard({ icon, title, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  const showBody = !collapsible || open
  const header = (
    <>
      <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#E6C878]">
        <span>{icon}</span> {title}
      </span>
      {collapsible && <span className="text-white/40 text-xs">{open ? '▾' : '▸'}</span>}
    </>
  )
  return (
    <div className="rounded-2xl bg-[#1F1913] border border-[#C8922A]/20 overflow-hidden">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors"
        >
          {header}
        </button>
      ) : (
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">{header}</div>
      )}
      {showBody && <div className="px-4 py-3">{children}</div>}
    </div>
  )
}

export function SideFact({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="shrink-0 pt-0.5 text-[10px] uppercase tracking-wider text-white/35">{label}</span>
      <span className="min-w-0 text-right text-xs text-white/85 wrap-break-word">{value || '—'}</span>
    </div>
  )
}

/**
 * In-call consultation notes — the same draft the appointment page edits
 * (PATCH /bookings/:id/astrologer-note). No save button: the draft
 * auto-saves as the astrologer types (debounced) and flushes on unmount,
 * and every save is written into the cached booking-details read so the
 * appointment page opens with the note already carried forward.
 */
export function HostNotesCard({ booking }) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState(booking.astrologer_note ?? '')
  const [status, setStatus] = useState('idle') // idle | saving | saved | error
  const finalized = isNoteFinalized(booking)

  const noteRef = useRef(note)
  noteRef.current = note
  const savedRef = useRef(booking.astrologer_note ?? '')
  const timerRef = useRef(null)
  // Whether the astrologer has typed here yet — gates the adopt-effect so a
  // background refetch can't overwrite in-call edits.
  const dirtyRef = useRef(false)

  // Adopt a newer note arriving from a background refetch (e.g. one the
  // appointment page autosaved just before the astrologer stepped into the
  // room) while nothing has been typed here yet — the appointment→in-call half
  // of the bidirectional carry-forward.
  useEffect(() => {
    if (!dirtyRef.current) {
      setNote(booking.astrologer_note ?? '')
      savedRef.current = booking.astrologer_note ?? ''
    }
  }, [booking.astrologer_note])

  const persist = async () => {
    const value = noteRef.current
    // The backend refuses empty notes ('required' rule); unchanged text
    // needs no round-trip.
    if (value === savedRef.current || !value.trim()) return
    setStatus('saving')
    try {
      await api.bookings.updateAstrologerNote(booking.id, value)
      savedRef.current = value
      // Carry the draft into the cached booking-by-id envelope — both pages
      // share this query key, so the appointment page mounts with the note.
      writeCachedAstrologerNote(queryClient, booking.id, value)
      setStatus('saved')
    } catch {
      setStatus('error')
    }
  }

  // Debounced autosave: 1.2s after typing stops; flush any pending text on
  // unmount (leaving the room / ending the call).
  useEffect(() => {
    if (finalized) return undefined
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(persist, 1200)
    return () => clearTimeout(timerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note, finalized])
  useEffect(() => () => { persist() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const statusLine = {
    idle: { Icon: LuNotebookPen, text: 'Auto-saves as you type.' },
    saving: { Icon: LuLoaderCircle, text: 'Saving…', spin: true },
    saved: { Icon: LuCheck, text: 'Saved' },
    error: { Icon: LuTriangleAlert, text: 'Could not save — will retry as you type.' },
  }[status]

  return (
    <HostSideCard title="Consultation Notes" icon={<LuNotebookPen className="w-3.5 h-3.5" />} collapsible>
      {finalized ? (
        <div>
          <p className="flex items-center gap-1.5 text-[10px] text-green-400/80 mb-2">
            <LuLock className="w-3 h-3 shrink-0" /> Finalized and shared with the customer.
          </p>
          <p className="text-xs leading-5 text-white/70 whitespace-pre-wrap">{booking.astrologer_note || '—'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Voice typing during the call — dictated phrases append to the
              same draft the debounced autosave persists. */}
          <div className="flex items-center justify-end">
            <DictationButton onTranscript={(text) => { dirtyRef.current = true; setNote((n) => appendSpokenText(n, text)) }} />
          </div>
          <textarea
            value={note}
            onChange={(e) => { dirtyRef.current = true; setNote(e.target.value) }}
            rows={6}
            placeholder="Write notes during the session — they save automatically and can be finalized from the appointment page…"
            className="w-full rounded-xl border border-[#C8922A]/20 bg-[#14100D] px-3 py-2.5 text-xs leading-5 text-white/85 placeholder:text-white/25 resize-y focus:outline-none focus:border-[#C8922A]"
          />
          <p className={`flex items-center gap-1.5 text-[10px] ${status === 'error' ? 'text-red-300' : status === 'saved' ? 'text-green-400/80' : 'text-white/35'}`}>
            <statusLine.Icon className={`w-3 h-3 shrink-0 ${statusLine.spin ? 'animate-spin' : ''}`} />
            {statusLine.text}
          </p>
        </div>
      )}
    </HostSideCard>
  )
}
